import { cleanPermalink } from './top-videos';
import { format } from 'date-fns';

import type { SeedingComment } from '../../seeding-report/lib/pick-comments';
import type { CommentCardImage } from '../../seeding-report/lib/render-comment-card';
import { fetchImageForDocx, type FetchedImage } from './docx-image';
import { formatMetricsLine, type VideoMetrics } from './format-metrics-ru';
import type { TopVideoPlatform } from './top-videos';

/**
 * Отчёт «Топ видео» в Word.
 *
 * Формат снят с файла «2026.09.16 топ видео.docx», который в медиа собирали
 * руками. На ролик — тема, ссылка, кадр, метрики и «Топовые комментарии»
 * карточками в вид приложения (см. seeding-report/lib/render-comment-card), как
 * скриншоты в образце. Длинный разбор ролика сюда не переносится: его
 * попросили не делать.
 */

// Размер кадра в образце — 200×267 px. Пропорции исходника сохраняются, так
// что вертикальный кадр 9:16 упрётся в высоту и станет уже.
export const IMAGE_BOX = { width: 200, height: 267 };

export type TopVideoRow = {
  permalink: string;
  /** Текст с обложки, распознанный при сборе отчёта. Может быть пустым. */
  theme: string;
  imageUrl: string;
  metrics: VideoMetrics;
  /** Лучшие комментарии после отсева. Нет или пусто — блока «Топовые комментарии» не будет. */
  comments?: SeedingComment[];
};

export type TopVideosSection = {
  accountTitle: string;
  platform: TopVideoPlatform;
  /** Уже отобранные и отсортированные ролики — см. pickTopVideos. */
  rows: TopVideoRow[];
};

export type TopVideosParams = {
  sections: TopVideosSection[];
  /** Карточки комментариев по id. Карточки нет — комментарий идёт таблицей. */
  cards?: Map<string, CommentCardImage>;
};

/** Ширина скриншота комментария в образце — 271 pt, то есть 361 px. */
export const COMMENT_CARD_PX = 361;

/** Размер карточки в документе: ширина как в образце, пропорции свои. */
export function cardSize(card: CommentCardImage): { width: number; height: number } {
  return { width: COMMENT_CARD_PX, height: Math.round((card.height * COMMENT_CARD_PX) / card.width) };
}

// Оформление снято с образца: Arial 14 pt, абзацы без отбивок, тема ролика —
// жирным с номером, ссылка и кадр глубже, строка метрик жирная, по ширине и с
// красной строкой. Размеры в twips (1440 на дюйм), шрифт — в полупунктах.
const FONT = 'Arial';
const FONT_SIZE = 28;
const INDENT_NUMBER = 720;
const INDENT_BODY = 1080;
const FIRST_LINE = 720;
// Цвет ссылки из темы образца (Office 2023). Стиль Hyperlink из библиотеки
// docx красит в старый синий 0563C1, поэтому цвет задан прямо на ссылке.
const LINK_COLOR = '467886';

// Запасной вид комментария, когда карточку не удалось нарисовать: тёмная
// таблица в цветах TikTok. Ширина как у скриншотов образца (~3,8 дюйма).
const CARD = {
  indent: 720,
  textCol: 5460,
  fill: '121212',
  divider: '2A2A2A',
  name: 'BDBDBD',
  body: 'FFFFFF',
  meta: '8A8A8A',
  size: 22,
};

/**
 * Шапка раздела — слово в слово как в образце, включая «и комментариев» и
 * написание «Tiktok». Для Instagram — по тому же шаблону.
 */
const HEADING_LINE = 'Анализ топовых видео и комментариев канала';

function sectionSubject(section: TopVideosSection): string {
  const kind = section.platform === 'tiktok' ? 'Tiktok' : 'Instagram';
  return `${kind}-канала «${section.accountTitle.replace(/^@/, '')}»`;
}

/**
 * Сам документ, без сети и без скачивания — отдельно, чтобы его можно было
 * собрать в тесте и проверить, что в файл попало и чего в нём нет.
 */
export async function buildTopVideosDocument(
  params: TopVideosParams,
  imageByRow: Map<TopVideoRow, FetchedImage | null> = new Map(),
) {
  const sections = params.sections.filter((s) => s.rows.length > 0);
  if (sections.length === 0) throw new Error('Нет данных для экспорта');

  const docx = await import('docx');
  const {
    AlignmentType,
    BorderStyle,
    Document,
    ExternalHyperlink,
    ImageRun,
    Paragraph,
    ShadingType,
    Table,
    TableCell,
    TableLayoutType,
    TableRow,
    TextRun,
    VerticalAlign,
    WidthType,
  } = docx;

  // Шрифт — на каждом фрагменте, а не только в умолчаниях документа, как и в
  // образце: Word понимает и то и другое, но Pages и просмотр macOS умолчания
  // игнорируют и показывают Times.
  const text = (
    value: string,
    options: { bold?: boolean; style?: string; color?: string; underline?: object } = {},
  ) => new TextRun({ text: value, font: FONT, size: FONT_SIZE, ...options });

  const blank = () => new Paragraph({ children: [] });
  const centered = (value: string, bold: boolean, pageBreakBefore = false) =>
    new Paragraph({
      alignment: AlignmentType.CENTER,
      pageBreakBefore,
      children: [text(value, { bold })],
    });

  const none = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
  const divider = { style: BorderStyle.SINGLE, size: 4, color: CARD.divider };

  const cardText = (value: string, color: string, bold = false, size = CARD.size) =>
    new TextRun({ text: value, font: FONT, size, color, bold });

  const commentTable = (comments: SeedingComment[]) =>
    new Table({
      indent: { size: CARD.indent, type: WidthType.DXA },
      width: { size: CARD.textCol, type: WidthType.DXA },
      columnWidths: [CARD.textCol],
      layout: TableLayoutType.FIXED,
      borders: { top: none, bottom: none, left: none, right: none, insideHorizontal: divider, insideVertical: none },
      rows: comments.map((c) => {
        const meta = [c.date, `♡ ${c.likeCount.toLocaleString('ru-RU')}`].filter(Boolean).join('   ');
        return new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: CARD.textCol, type: WidthType.DXA },
              shading: { fill: CARD.fill, type: ShadingType.CLEAR, color: 'auto' },
              margins: { top: 90, bottom: 90, left: 110, right: 110 },
              verticalAlign: VerticalAlign.TOP,
              children: [
                new Paragraph({ children: [cardText(c.nickname || c.username, CARD.name, true, CARD.size - 2)] }),
                new Paragraph({ spacing: { before: 40 }, children: [cardText(c.text, CARD.body)] }),
                new Paragraph({ spacing: { before: 60 }, children: [cardText(meta, CARD.meta, false, CARD.size - 4)] }),
              ],
            }),
          ],
        });
      }),
    });

  // Как в образце: пустая строка, жирный заголовок с красной строкой и по
  // абзацу на каждый скриншот.
  const commentsBlock = (comments: SeedingComment[] | undefined) => {
    if (!comments || comments.length === 0) return [];
    const head = [
      blank(),
      new Paragraph({
        indent: { firstLine: FIRST_LINE },
        keepNext: true,
        children: [text('Топовые комментарии', { bold: true })],
      }),
    ];
    const drawn = comments.map((c) => params.cards?.get(c.id));
    if (!drawn.every(Boolean)) return [...head, commentTable(comments)];
    return [
      ...head,
      ...drawn.map((card) => {
        const size = cardSize(card as CommentCardImage);
        return new Paragraph({
          indent: { firstLine: FIRST_LINE },
          children: [
            new ImageRun({
              data: (card as CommentCardImage).dataUrl.split(',')[1],
              type: 'png',
              transformation: size,
            }),
          ],
        });
      }),
    ];
  };

  const videoBlock = (row: TopVideoRow, index: number) => {
    const heading = [`${index + 1}.`, row.theme].filter(Boolean).join(' ');
    const image = imageByRow.get(row) ?? null;

    return [
      new Paragraph({
        // Номер висит слева, перенос темы выравнивается по тексту, а не по цифре.
        indent: { left: INDENT_BODY, hanging: INDENT_BODY - INDENT_NUMBER },
        keepNext: true,
        children: [text(heading, { bold: true })],
      }),
      new Paragraph({
        indent: { left: INDENT_BODY },
        keepNext: true,
        children: [
          new ExternalHyperlink({
            link: cleanPermalink(row.permalink),
            children: [
              text(cleanPermalink(row.permalink), { style: 'Hyperlink', color: LINK_COLOR, underline: { type: 'single' } }),
            ],
          }),
          // Хвост обычным текстом — как в образце. Без него просмотр macOS
          // теряет отступ у абзаца, где кроме ссылки ничего нет.
          text(' '),
        ],
      }),
      ...(image
        ? [
            new Paragraph({
              indent: { left: INDENT_BODY },
              keepNext: true,
              children: [
                new ImageRun({
                  data: image.data,
                  type: image.type,
                  transformation: { width: image.width, height: image.height },
                }),
              ],
            }),
          ]
        : []),
      new Paragraph({
        alignment: AlignmentType.JUSTIFIED,
        indent: { firstLine: FIRST_LINE },
        children: [text(formatMetricsLine(row.metrics), { bold: true })],
      }),
      ...commentsBlock(row.comments),
      blank(),
    ];
  };

  // Каждый аккаунт — со своей страницы и со своим заголовком: так файл по
  // нескольким аккаунтам читается как несколько отчётов образца подряд.
  const body = sections.flatMap((section, i) => [
    centered(HEADING_LINE, true, i > 0),
    centered(sectionSubject(section), true),
    blank(),
    ...section.rows.flatMap(videoBlock),
  ]);

  return new Document({
    styles: {
      default: {
        document: {
          run: { font: FONT, size: FONT_SIZE },
          paragraph: { spacing: { after: 0 } },
        },
      },
    },
    sections: [{ children: body }],
  });
}

export async function generateTopVideosDocx(params: TopVideosParams): Promise<void> {
  // Кадры тянем все разом: их немного (топ на аккаунт), а по одному это
  // заметная задержка на сетевых промахах.
  const rows = params.sections.flatMap((s) => s.rows);
  const fetched = await Promise.all(
    rows.map((row) => (row.imageUrl ? fetchImageForDocx(row.imageUrl, IMAGE_BOX) : Promise.resolve(null))),
  );
  const doc = await buildTopVideosDocument(
    params,
    new Map(rows.map((row, i) => [row, fetched[i]])),
  );

  const { Packer } = await import('docx');
  // Имя как у образца: «2026.09.16 топ видео.docx».
  const filename = `${format(new Date(), 'yyyy.MM.dd')} топ видео`;
  const blob = await Packer.toBlob(doc);
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.docx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}
